import { useState, useEffect, useRef, useLayoutEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

function TexteTronque({ texte, lignes = 5, couleurLien = "#1976d2" }) {
  const ref = useRef(null);
  const [ouvert, setOuvert] = useState(false);
  const [tronque, setTronque] = useState(false);

  useLayoutEffect(() => {
    const verifier = () => {
      const el = ref.current;
      if (el && !ouvert) setTronque(el.scrollHeight > el.clientHeight + 1);
    };
    verifier();
    window.addEventListener("resize", verifier);
    return () => window.removeEventListener("resize", verifier);
  }, [texte, ouvert]);

  return (
    <>
      <MDTypography
        ref={ref}
        variant="body2"
        color="text"
        sx={{
          whiteSpace: "pre-line",
          wordBreak: "break-word",
          ...(!ouvert && {
            display: "-webkit-box",
            WebkitLineClamp: lignes,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }),
        }}
      >
        {texte}
      </MDTypography>
      {(tronque || ouvert) && (
        <MDTypography
          component="button"
          type="button"
          variant="caption"
          fontWeight="bold"
          onClick={() => setOuvert((o) => !o)}
          sx={{
            mt: 0.5,
            p: 0,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: couleurLien,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {ouvert ? "Voir moins" : "Voir plus"}
        </MDTypography>
      )}
    </>
  );
}

function Feedbacks() {
  const [feedbacks, setFeedbacks] = useState([]);

  useEffect(() => {
    api.get("/feedbacks")
      .then((res) => setFeedbacks(res.data))
      .catch(console.error);
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Retours des fêtes
                </MDTypography>
                <MDTypography variant="caption" color="white">
                  {feedbacks.length} retour(s)
                </MDTypography>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {feedbacks.map((f) => {
                    const benefice = (parseFloat(f.recettes) || 0) - (parseFloat(f.cout_total) || 0);
                    const diff = f.nb_participants && f.nb_personnes_estime
                      ? f.nb_participants - f.nb_personnes_estime
                      : null;

                    return (
                      <Grid item xs={12} md={6} lg={4} key={f.id}>
                        <Card sx={{ p: 2, border: "1px solid #eee", borderRadius: 3 }}>

                          {/* Header */}
                          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1565c0" }}>
                            {f.directeur_ville}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={0.5}>
                            {f.directeur_nom} — {f.occasion}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block" mb={1}>
                            {f.date_fete ? f.date_fete.slice(0, 10).split("-").reverse().join("/") : "—"}
                            {" — "}{f.lieu}
                          </MDTypography>

                          <Divider />

                          {/* Participants */}
                          <MDBox sx={{ bgcolor: "#e3f2fd", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#1565c0" }} display="block" mb={0.5}>
                              Participants
                            </MDTypography>
                            <MDBox display="flex" justifyContent="space-between">
                              <MDBox>
                                <MDTypography variant="caption" color="text" display="block">
                                  Estimé : <strong>{f.nb_personnes_estime || "—"}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Réel : <strong>{f.nb_participants || "—"}</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  Nouveaux : <strong>{f.nb_nouveaux || "—"}</strong>
                                </MDTypography>
                              </MDBox>
                              {diff !== null && (
                                <Chip
                                  label={`${diff >= 0 ? "+" : ""}${diff}`}
                                  size="small"
                                  sx={{
                                    bgcolor: diff >= 0 ? "#43a047" : "#e53935",
                                    color: "white",
                                    fontWeight: "bold",
                                    alignSelf: "center",
                                  }}
                                />
                              )}
                            </MDBox>
                          </MDBox>

                          {/* Durée */}
                          {f.duree_heures && (
                            <MDBox display="flex" alignItems="center" gap={0.5} mb={1}>
                              <Icon sx={{ fontSize: 15, color: "#546e7a" }}>schedule</Icon>
                              <MDTypography variant="body2" color="text">
                                Durée : <strong>{f.duree_heures}h</strong>
                              </MDTypography>
                            </MDBox>
                          )}

                          {/* Finances */}
                          {(f.cout_total || f.recettes) && (
                            <MDBox sx={{ bgcolor: benefice >= 0 ? "#e8f5e9" : "#ffebee", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                              <MDTypography variant="caption" fontWeight="bold" sx={{ color: benefice >= 0 ? "#2e7d32" : "#c62828" }} display="block" mb={0.5}>
                                Finances
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                Coût : <strong>{f.cout_total ? `${parseFloat(f.cout_total).toFixed(2)} €` : "—"}</strong>
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                Recettes : <strong>{f.recettes ? `${parseFloat(f.recettes).toFixed(2)} €` : "—"}</strong>
                              </MDTypography>
                              <MDTypography variant="caption" fontWeight="bold" sx={{ color: benefice >= 0 ? "#2e7d32" : "#c62828" }} display="block">
                                {benefice >= 0 ? "Bénéfice" : "Déficit"} : {benefice >= 0 ? "+" : ""}{benefice.toFixed(2)} €
                              </MDTypography>
                            </MDBox>
                          )}

                          {/* Axe amélioration */}
                          {f.axe_amelioration && (
                            <MDBox sx={{ bgcolor: "#fff3e0", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                              <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#e65100" }} display="block" mb={0.5}>
                                Axe d&apos;amélioration
                              </MDTypography>
                              <TexteTronque texte={f.axe_amelioration} couleurLien="#e65100" />
                            </MDBox>
                          )}

                          {/* Observations */}
                          {f.observations && (
                            <MDBox sx={{ bgcolor: "#f8f9fa", borderRadius: 2, p: 1.5 }}>
                              <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                                Observations
                              </MDTypography>
                              <TexteTronque texte={f.observations} />
                            </MDBox>
                          )}

                          <MDTypography variant="caption" color="text" display="block" mt={1}>
                            Soumis le : {new Date(f.created_at).toLocaleDateString("fr-FR")}
                          </MDTypography>
                        </Card>
                      </Grid>
                    );
                  })}
                  {feedbacks.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucun retour pour le moment.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Feedbacks;
